<template lang="pug">
  .b-spec-requests-panel(data-testid="requests-panel")
    ._head
      ._title Запросы КП
      button._close(type="button" data-testid="requests-close" @click="$emit('close')") ×

    ._empty(v-if="!requests.length" data-testid="requests-empty") Запросов пока нет

    ._item(v-for="request in requests" :key="request.id" data-testid="requests-item")
      ._item-head
        button._toggle(type="button" data-testid="requests-item-toggle" @click="toggle(request)") {{ opened.includes(request.id) ? '−' : '+' }}
        span._supplier(data-testid="requests-item-supplier") {{ request.supplier && request.supplier.name }}
        span._status(data-testid="requests-item-status") {{ requestStatusTitle(request.status) }}
        span._date(data-testid="requests-item-date") {{ date(request.createdAt) }}
      ._item-info
        span(data-testid="requests-item-items-count") позиций: {{ request.itemsCount }}
        span(data-testid="requests-item-offers-count") откликов: {{ request.offersCount }}
        span(data-testid="requests-item-rework-count") на доработке: {{ request.reworkCount }}
      ._files(v-if="(request.quoteFiles || []).length")
        a._file(
          v-for="file in request.quoteFiles"
          :key="file.id"
          data-testid="requests-item-file"
          :href="file.url"
          target="_blank"
          rel="noopener noreferrer"
          ) {{ file.name }}

      ._positions(v-if="opened.includes(request.id)")
        ._position(v-for="row in request.items" :key="row.id" data-testid="requests-position")
          input._position-check(
            v-if="!readOnly"
            type="checkbox"
            data-testid="requests-position-checkbox"
            :checked="isChecked(request, row)"
            @change="check(request, row, $event.target.checked)"
            )
          button._position-name(type="button" data-testid="requests-position-name" @click="$emit('goto', row.item)") {{ row.item && row.item.name }}
          span._position-status(data-testid="requests-position-status") {{ itemStatusTitle(row.item && row.item.status) }}
          span._position-offers(data-testid="requests-position-offers") {{ offersText(row) }}
          span._position-rework(v-if="row.isRework" data-testid="requests-position-rework") на доработке: {{ row.reworkComment || 'без комментария' }}
          span._position-rerequested(v-if="row.reRequestedAt" data-testid="requests-position-rerequested") запрошено повторно {{ date(row.reRequestedAt) }}

        ._rework(v-if="!readOnly && checkedOf(request).length")
          textarea._rework-comment(data-testid="requests-rework-comment" v-model="comments[request.id]" placeholder="Комментарий к доработке — обязателен")
          Btn(blue sm data-testid="requests-rework-submit" @click="submitRework(request)") Отправить на доработку
        button._rework-btn(
          v-else-if="!readOnly && hasReworkable(request)"
          type="button"
          data-testid="requests-rework"
          @click="checkAllReworkable(request)"
          ) На доработку ({{ reworkableOf(request).length }})
    ._error(v-if="error") {{ error }}
</template>

<script>
import specRequestApi from '@/api/specRequest';
import { apiRun } from '@/model/apiRun';
import {
  requestStatusTitle, itemStatusTitle, offerStateTitle, date,
} from '@/utils/spec';

/**
 * Боковая панель «Запросы» (ТЗ 6.2): список запросов КП спецификации со статусами, файлами КП и
 * раскрываемыми позициями; отсюда же одна операция «На доработку» по отмеченным позициям с
 * обязательным комментарием. Сгенерировано Claude.
 */
export default {
  name: 'SpecRequestsPanel',
  props: {
    requests: { type: Array, default: () => [] },
    /** Архивный проект: «На доработку» недоступна, список открыт на чтение (ТЗ 6.1). */
    readOnly: Boolean,
  },
  data() {
    return {
      opened: [],
      checked: {},
      comments: {},
      error: '',
    };
  },
  methods: {
    requestStatusTitle,
    itemStatusTitle,
    date,
    toggle(request) {
      this.opened = this.opened.includes(request.id)
        ? this.opened.filter((id) => id !== request.id)
        : [...this.opened, request.id];
    },
    offersText(row) {
      const states = (row.offers ?? []).map((offer) => offerStateTitle(offer.state));
      return states.length ? states.join(', ') : 'откликов нет';
    },
    /** На доработку можно вернуть позицию запроса со откликами в статусе «Есть отклики» (ТЗ 2.5). */
    reworkableOf(request) {
      return (request.items ?? []).filter(
        (row) => row.item?.status === 'has_offers' && (row.offers ?? []).some((o) => o.state === 'new'),
      );
    },
    hasReworkable(request) {
      return this.reworkableOf(request).length > 0;
    },
    checkedOf(request) {
      return this.checked[request.id] ?? [];
    },
    isChecked(request, row) {
      return this.checkedOf(request).includes(row.item?.id);
    },
    check(request, row, value) {
      const current = this.checkedOf(request);
      const next = value
        ? [...current, row.item.id]
        : current.filter((id) => id !== row.item.id);
      this.checked = { ...this.checked, [request.id]: next };
    },
    checkAllReworkable(request) {
      this.checked = {
        ...this.checked,
        [request.id]: this.reworkableOf(request).map((row) => row.item.id),
      };
    },
    async submitRework(request) {
      const comment = (this.comments[request.id] ?? '').trim();
      if (!comment) {
        this.error = 'Комментарий к доработке обязателен';
        return;
      }
      this.error = '';
      const result = await apiRun(
        () => specRequestApi.rework(request.id, { items: this.checkedOf(request), comment }),
        'Не удалось отправить на доработку',
      );
      if (!result) return;
      this.checked = { ...this.checked, [request.id]: [] };
      this.comments = { ...this.comments, [request.id]: '' };
      this.$emit('reworked');
    },
  },
};
</script>

<style scoped lang="scss">
.spec-requests-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 520px;
  max-width: 100%;
  z-index: 9000;
  background: var(--color-white);
  box-shadow: -6px 0 24px rgba(0, 0, 0, 0.14);
  padding: 20px;
  overflow-y: auto;
  box-sizing: border-box;

  &__head {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
  }

  &__title {
    font-size: 20px;
    font-weight: 700;
    flex-grow: 1;
  }

  &__close {
    border: none;
    background: none;
    font-size: 24px;
    line-height: 1;
    cursor: pointer;
  }

  &__empty {
    color: var(--color-middle-gray);
  }

  &__item {
    padding: 12px 0;
    border-bottom: 1px solid var(--color-super-light);
  }

  &__item-head {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__toggle {
    width: 22px;
    height: 22px;
    border: 1px solid var(--color-super-light);
    border-radius: 4px;
    background: var(--color-white);
    cursor: pointer;
    font: inherit;
  }

  &__supplier {
    font-weight: 700;
    flex-grow: 1;
  }

  &__status,
  &__date {
    font-size: 12px;
    color: var(--color-middle-gray);
  }

  &__item-info {
    display: flex;
    gap: 14px;
    margin: 6px 0 0 32px;
    font-size: 12px;
    color: var(--color-middle-gray);
  }

  &__files {
    display: flex;
    flex-direction: column;
    margin: 6px 0 0 32px;
  }

  &__file {
    font-size: 12px;
    color: var(--color-accent);
  }

  &__positions {
    margin: 10px 0 0 32px;
  }

  &__position {
    display: grid;
    grid-template-columns: 20px 1fr;
    gap: 2px 8px;
    padding: 6px 0;
    border-top: 1px solid var(--color-super-light);
    font-size: 12px;
  }

  &__position-check {
    width: 16px;
    height: 16px;
    margin: 0;
  }

  &__position-name {
    border: none;
    background: none;
    font: inherit;
    text-align: left;
    color: var(--color-accent);
    cursor: pointer;
    padding: 0;
  }

  &__position-status,
  &__position-offers,
  &__position-rework,
  &__position-rerequested {
    grid-column: 2;
    color: var(--color-middle-gray);
  }

  &__position-rework {
    color: var(--color-orange);
  }

  &__rework {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 10px;
  }

  &__rework-comment {
    box-sizing: border-box;
    width: 100%;
    min-height: 60px;
    padding: 6px 8px;
    border: 1px solid var(--color-super-light);
    border-radius: 6px;
    font: inherit;
    font-size: 12px;
  }

  &__rework-btn {
    margin-top: 10px;
    border: none;
    background: none;
    font: inherit;
    font-size: 13px;
    color: var(--color-accent);
    cursor: pointer;
    padding: 0;
  }

  &__error {
    color: var(--color-red);
    margin-top: 10px;
  }
}
</style>
